// Aba "Conciliação Bancária" do /caixa (DA-214) — funde "Recebimentos Pix (MP)" + "Recebimentos
// Cartão" numa lista só e adiciona a fileira de vendas eletrônicas finalizadas SEM recebimento
// capturado. Princípio de negócio: toda venda Pix/cartão precisa ter a conciliação confirmada; o
// que não tiver fica visível até alguém resolver, independente de qual banco recebeu (ou se não
// recebeu). Filtro segmentado Todos / Pix / Cartão. Conciliados ocultos por padrão.
//
// Duas naturezas de linha:
//  1. Recebimento capturado: Pix do MP (mp_recebimentos) e cartão Point (point_cobrancas pago).
//     Casado → conciliado (✓). Capturado sem casar → Vincular (só Pix) / Conferir.
//  2. Venda finalizada SEM pagamento relacionado (Desconhecido, selo "Sem recebimento"); sem
//     Vincular; só Conferir → modal que EXIGE banco (seletor) + agência/conta (opcionais).
//  A coluna Conta saiu em 24/10/2026 (pedido dele) — so a Conciliacao Pgto mostra conta.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Loader2, Check, Link, X, FileText, ShieldCheck, UserPlus, UserMinus, AlertTriangle, Undo2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { ModalBuscarCliente } from '../../components/orcamento/ModalBuscarCliente';
import { ModalSenhaGerencial } from '../../components/orcamento/ModalSenhaGerencial';
import { useSocket } from '../../lib/socket';
import { useToast } from '../../components/Toast';
import { useAviso } from '../../components/Aviso';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';
import { formatMoeda, formatDataHoraSP, formatHoraSP, formatDataSP, parseSPDate, TOLERANCIA_CENTAVO_CALIBRACAO } from '@fiodecobre/shared';
import { tocarBeep } from '../atendimento/lib/beep';
import { SeloEstorno, classificarCartaoMp, ComprovanteEstornoModal, AuditorCartaoModal } from '../../components/caixa/CartaoEstorno';
import { ModalEstornoPix, ComprovanteDevolucaoModal, type EstornoRealizado } from '../../components/caixa/EstornoPix';
import { TitulosParaReceber, totalTitulos, type TituloParaReceber } from '../../components/contas-receber/TitulosParaReceber';
import { gerarReciboHTML, imprimirHTML } from '../contas-receber/utils/extrato-html';
import { useAuth } from '../../lib/auth';

type Segmento = 'todos' | 'pix' | 'cartao';

// ---------- Pix (mp_recebimentos) ----------
interface VinculoInfo { vinculo_id: number; orcamento_id: number; numero: string; venda_numero: string | null; cliente_nome: string; vendedor_nome: string | null; valor: string; }
interface RecebimentoPix {
  id: number;
  tipo?: 'recebimento' | 'devolucao';
  mp_payment_id: string;
  valor: string;
  valor_devolvido?: string | null;
  pagador_nome: string | null;
  pagador_nome_exibicao: string | null;
  pagador_doc: string | null;
  descricao: string | null;
  status: string;
  data_pagamento: string | null;
  conferido: boolean;
  conferido_em: string | null;
  conferido_por_nome: string | null;
  criado_em: string;
  valor_vinculado: string | null;
  vinculos: VinculoInfo[] | null;
  cliente_conta_nome?: string | null;
  cliente_conta_vinculo_id?: number | null;
  cliente_conta_cliente_id?: number | null;
  conta_identificavel?: boolean;
  // [DA-549] A transferencia que EXPLICA este Pix: ele nao era dinheiro de cliente, era a loja
  // movendo dinheiro entre contas proprias. Quando existe, a coluna CONCILIAÇÃO para de ficar muda.
  transferencia_id?: number | null;
  transferencia_origem_nome?: string | null;
  transferencia_destino_nome?: string | null;
  mp_recebimento_id?: number;
  parent_mp_payment_id?: string;
  parent_data_pagamento?: string | null;
  // Linha de devolucao (DA-506): quem autorizou e por onde, e o que o comprovante precisa pra
  // ser reaberto/reenviado depois que o modal do desfecho fechou.
  mp_refund_id?: string | null;
  origem?: 'externa' | 'sistema' | null;
  autor_nome?: string | null;
  cliente_id?: number | null;
  cliente_nome?: string | null;
  parent_saldo_restante?: number | null;
}
interface PedidoPix {
  id: number;
  numero: string;
  venda_numero?: string | null;
  cliente_nome: string;
  total: string;
  valor_pix_declarado: string;
  valor_pix_vinculado: string;
  pix_validado_admin: boolean;
  atualizado_em: string;
  finalizada_em?: string | null;
  valor_crediario?: string;
  eh_crediario?: boolean;
  /** Parte do total sem forma declarada (so pedido no caixa) — tambem recebe Pix. */
  valor_nao_declarado?: string;
  /** Parte em dinheiro (so pedido no caixa) — recebe Pix e vira Pix ao vincular. Cartao nunca. */
  valor_dinheiro?: string;
}

// ---------- Cartão (point_cobrancas) ----------
interface RecebimentoCartao {
  id: number;
  orcamento_id: number;
  numero: string;
  venda_numero: string | null;
  cliente_nome: string;
  vendedor_nome: string | null;
  valor: string;
  valor_pago: string | null;
  valor_liquido: string | null;
  card_bandeira: string | null;
  card_parcelas: number;
  card_nsu: string | null;
  card_cod_autoriz: string | null;
  mp_payment_id: string | null;
  pago_em: string | null;
  conferido: boolean;
  conferido_em: string | null;
  conferido_por_nome: string | null;
  cp_taxa_id: number | null;
  status?: string; // 'pago' | 'desconciliado' (desfeito manualmente)
  // Espelho fiel do estorno no MP (Fase 1) — o vigia preenche; classificarCartaoMp deriva o selo.
  estorno_valor?: string | null;
  estorno_mp_id?: string | null;
  estorno_em?: string | null;
  mp_status?: string | null;
  estorno_verificado_em?: string | null;
  // Quem bancou o juros do parcelamento (DA-516). null = 1x, ou cobrança anterior à regra — a regra
  // vale daqui pra frente, então cobrança velha fica SEM selo em vez de receber um palpite.
  parcelamento_custo?: 'cliente' | 'vendedor' | 'desconhecido' | null;
  valor_esperado?: string | null;
}

// ---------- Estorno pendente (DA-280) — cartão de venda CANCELADA, dinheiro ainda retido na loja ----------
interface EstornoPendente {
  id: number;
  orcamento_id: number | null;
  valor: string;
  valor_pago: string | null;
  card_bandeira: string | null;
  card_parcelas: number | null;
  mp_payment_id: string | null;
  pago_em: string | null;
  atualizado_em: string | null;
  orcamento_numero: string | null;
  venda_numero: string | null;
  cliente_nome: string;
}

// ---------- Desconhecido (venda finalizada sem recebimento) ----------
export interface Desconhecido {
  orcamento_id: number;
  venda_id: number | null;
  numero: string;
  venda_numero: string | null;
  cliente_nome: string;
  vendedor_nome: string | null;
  forma: 'pix' | 'cartao';
  valor: string;
  finalizada_em: string | null;
  atualizado_em: string | null;
  // Conta escolhida na finalizacao (ex.: "Infinitepay"). Toda venda finalizada tem (medido 24/10).
  conta_declarada?: string | null;
}
export interface ContaBanco { id: number; nome: string; }

const hojeISO = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
const diaISO = (val: string | Date | null | undefined): string | null => {
  const d = parseSPDate(val);
  return d ? new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d) : null;
};
function rotuloMomento(val: string | Date | null | undefined, diaFiltro: string): string {
  const f = formatDataHoraSP(val);
  if (f === '—') return f;
  if (diaISO(val) === diaFiltro) { const p = f.split(' '); return p[p.length - 1] || f; }
  return `${formatDataSP(val)} ${formatHoraSP(val)}`;
}

function valorLiquidoPix(r: RecebimentoPix): number { return Math.max(0, Number(r.valor) - Number(r.valor_devolvido ?? 0)); }
function valorDisponivelPix(r: RecebimentoPix): number { return Math.max(0, valorLiquidoPix(r) - Number(r.valor_vinculado ?? 0)); }

// Selo nunca quebra linha (pedido dele, 24/10/2026): "Sem recebimento" partido em dois virava
// dois blocos escuros soltos na coluna Status. A tabela abre a largura que o selo precisa.
function pillRecebido() {
  return <span style={{ backgroundColor: '#fde68a', color: '#000', fontWeight: 700, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>Recebido</span>;
}
function pillConciliado() {
  return <span style={{ backgroundColor: '#fde68a', color: '#000', fontWeight: 700, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>Conciliado ✓</span>;
}
function pillDevolucao() {
  return <span style={{ backgroundColor: '#92400e', color: '#fff', fontWeight: 700, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>Devolução</span>;
}
// Numero da venda clicavel: abre o orcamento dela, e a seta de voltar do orcamento traz de volta
// pra Conciliacao (o /caixa escolhe a aba pelo state). A busca de Orcamentos nao acha pelo VND,
// entao sem isto a venda que a linha cita so se encontrava pelo nome do cliente.
function LinksVendas({ vinculos }: { vinculos: VinculoInfo[] }) {
  const navigate = useNavigate();
  return (
    <>
      {vinculos.map((v, i) => (
        <span key={v.vinculo_id}>
          {i > 0 && ', '}
          <a href={`/orcamentos/${v.orcamento_id}`}
            onClick={(e) => {
              // Ctrl/meio-clique seguem abrindo em outra aba, pelo href.
              if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate(`/orcamentos/${v.orcamento_id}`, { state: { from: '/caixa', fromState: { aba: 'conciliacao' } } });
            }}
            title={`Abrir ${v.numero} (${v.cliente_nome})`}
            style={{ color: 'inherit', textDecoration: 'underline' }}>
            {v.venda_numero ?? v.numero}
          </a>
        </span>
      ))}
    </>
  );
}
function pillCancelado() {
  return <span style={{ backgroundColor: 'var(--danger, #dc2626)', color: '#fff', fontWeight: 700, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>Cancelado</span>;
}
// Venda finalizada sem recebimento capturado: o selo diz a conta que a venda DECLAROU (pedido dele,
// 24/10/2026: "nao seria melhor mostrar a maquininha no lugar de Sem recebimento?"). O texto antigo
// e' so defesa: medido em 24/10, 0 de 2.499 pagamentos Pix/cartao finalizados desde 11/05 sem conta.
function pillDesconhecido(conta?: string | null) {
  return <span title={conta ? `Venda finalizada em ${conta} — o sistema não enxerga esse recebimento; confira no extrato` : undefined} style={{ backgroundColor: '#1f2937', color: '#fff', fontWeight: 700, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>{conta ?? 'Sem recebimento'}</span>;
}
// Quem bancou o juros do parcelamento (DA-516). Aparece em TODA cobrança parcelada, não só na
// errada — é a conferência que permite responder "esse recebimento está certo?" sem sair da tela.
//
// Só o VENDEDOR gasta cor (laranja sólido + preto, a cor de atenção da casa). "Parc Cliente" é o
// estado normal e correto: pintado de laranja também, ele viraria a cor de fundo da lista e no dia
// do problema real ninguém distinguiria o alarme — é a [DA-410] (selo sempre aceso ensina o
// operador a ignorar a cor). 'desconhecido' é cinza e diz "não confirmado", nunca acusa: o valor
// pode ter vindo do fallback do MP, que é indistinguível de um Parcelado Vendedor real.
function pillParcelamento(custo: 'cliente' | 'vendedor' | 'desconhecido') {
  const base = { fontWeight: 700, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' } as const;
  if (custo === 'vendedor') {
    return <span style={{ ...base, backgroundColor: '#f97316', color: '#000' }}>Parc Vendedor</span>;
  }
  if (custo === 'cliente') {
    return <span style={{ ...base, backgroundColor: 'var(--input-bg, #e5e7eb)', color: 'var(--text-color)', border: '1px solid var(--input-border, #d1d5db)' }}>Parc Cliente</span>;
  }
  return <span style={{ ...base, backgroundColor: 'var(--input-bg, #e5e7eb)', color: 'var(--text-muted)', border: '1px dashed var(--input-border, #d1d5db)' }}>Parc não confirmado</span>;
}
// Cartão capturado cujo pedido NUNCA virou venda (ver `semVenda` no filtro): o dinheiro entrou e
// não tem venda pra sustentar. É pendência, não erro (laranja de atenção, não o vermelho de falha).
function pillSemVenda() {
  return <span style={{ backgroundColor: '#f97316', color: '#000', fontWeight: 700, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>Sem venda</span>;
}
// Conteúdo da coluna "Vendedor" — o vendedor do título da linha. "—" quando não há título (Pix sem
// vínculo, devolução): sem venda casada o sistema não sabe quem vendeu.
function celulaVendedor(nome: string | null | undefined) {
  if (!nome) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return <span>{nome}</span>;
}

// ---------- Modal Vincular/Conferir do Pix (reaproveitado da aba antiga) ----------
function VincularModal({ recebimento, modo, podeDesvincular, onClose, onVinculado, onConferido }: {
  recebimento: RecebimentoPix;
  modo: 'vincular' | 'conferir';
  podeDesvincular: boolean;
  onClose: () => void;
  onVinculado: () => void;
  onConferido: () => void;
}) {
  const toast = useToast();
  const aviso = useAviso();
  const dialog = useDialog();
  const { usuario } = useAuth();
  // Aba do alvo do vínculo: "Pedido para Finalizar" (ainda no caixa) OU "Vendas finalizadas" (já
  // finalizada, de QUALQUER data — DA-299). Antes eram dois botões distintos (Vincular/Conferir) com
  // listas separadas — agora um modal só com as duas abas, pra achar a venda esteja ela onde estiver.
  // O botão de entrada só define a aba inicial.
  const [aba, setAba] = useState<'pedidos' | 'vendas' | 'titulos'>(modo === 'conferir' ? 'vendas' : 'pedidos');
  const endpoint = aba === 'vendas' ? '/api/mercadopago/vendas-finalizadas-pix' : '/api/mercadopago/pedidos-pix';
  const [pedidos, setPedidos] = useState<PedidoPix[] | null>(null);
  const [busca, setBusca] = useState('');

  // Aba "Contas a Receber": o Pix pode ser o pagamento de um FIADO, não de uma venda do balcão. Só
  // existe quando o pagador foi identificado (a conta de origem está amarrada a um cliente) — sem
  // saber de QUEM é o dinheiro não dá pra baixar título de ninguém. Quem baixa é o mesmo endpoint da
  // tela de Contas a Receber (baixar-lote), que amarra este Pix a cada título: dinheiro tem um
  // caminho só. A tabela é a peça 017, a mesma das duas telas.
  const clienteId = recebimento.cliente_conta_cliente_id ?? null;
  const [titulos, setTitulos] = useState<TituloParaReceber[] | null>(null);
  const [valoresTit, setValoresTit] = useState<Record<number, number>>({});
  const [selTit, setSelTit] = useState<Set<number>>(new Set());
  const [baixandoTit, setBaixandoTit] = useState(false);
  const [vinculando, setVinculando] = useState<number | null>(null);
  const [removendo, setRemovendoId] = useState<number | null>(null);
  const [confirmandoSemVinculo, setConfirmandoSemVinculo] = useState(false);
  const [conferindo, setConferindo] = useState(false);
  const [alocadoLocal, setAlocadoLocal] = useState(0);

  const recarregar = useCallback(() => {
    if (aba === 'titulos') return; // a aba de fiado tem lista propria (titulos do cliente)
    api.get<PedidoPix[]>(endpoint).then(setPedidos).catch((e: any) => aviso.erro(e.message));
  }, [aba, endpoint, toast]);
  useEffect(() => { recarregar(); }, [recarregar]);
  // Troca de aba: limpa a lista (mostra o loading) enquanto o endpoint da nova aba carrega.
  useEffect(() => { setPedidos(null); }, [aba]);

  const dispPix = valorDisponivelPix(recebimento);

  // Titulos em aberto do cliente que pagou. Pre-marca os MAIS ANTIGOS ate o Pix acabar (a ordem que o
  // financeiro usa: quita o vencido primeiro) — no caso comum o Pix cobre tudo e o operador so confirma.
  const carregarTitulos = useCallback(() => {
    if (!clienteId) return;
    setTitulos(null);
    api.get<TituloParaReceber[]>('/api/contas-receber', { query: { status: 'pendente', cliente_id: String(clienteId) } })
      .then((lista) => {
        const ordenada = [...lista].sort((a, b) => (a.data_vencimento < b.data_vencimento ? -1 : 1));
        setTitulos(ordenada);
        const marcados = new Set<number>();
        let sobra = dispPix;
        for (const t of ordenada) {
          const v = Number(t.valor);
          if (v > sobra + 0.005) break;
          marcados.add(t.id);
          sobra = +(sobra - v).toFixed(2);
        }
        setSelTit(marcados);
        setValoresTit(Object.fromEntries(ordenada.map((t) => [t.id, marcados.has(t.id) ? Number(t.valor) : 0])));
      })
      .catch((e: any) => { aviso.erro(e.message); setTitulos([]); });
  }, [clienteId, dispPix, toast]);
  useEffect(() => { if (aba === 'titulos') carregarTitulos(); }, [aba, carregarTitulos]);

  async function receberTitulos() {
    const linhas = (titulos ?? [])
      .filter((t) => selTit.has(t.id) && (valoresTit[t.id] ?? 0) > 0.005)
      .map((t) => ({ id: t.id, valor_pago: valoresTit[t.id]! }));
    if (linhas.length === 0) { aviso.erro('Marque ao menos um titulo'); return; }
    setBaixandoTit(true);
    try {
      // Sem forma/caixa: o backend deriva do recebimento (o dinheiro JA caiu no Mercado Pago — casar
      // nao cria entrada nova no caixa, so amarra o que entrou).
      const r = await api.post<{ baixados: unknown[]; total_recebido: number }>('/api/contas-receber/baixar-lote', {
        titulos: linhas,
        mp_recebimento_id: recebimento.id,
        gatilho: 'conciliacao', // so pra auditoria: a mesma rota atende esta aba e o lote da tela de CR
      });
      toast.sucesso(`${r.baixados.length} titulo(s) baixado(s) — ${formatMoeda(r.total_recebido)} conciliado`);
      // Recibo na hora, igual a baixa pela tela de Contas a Receber — a porta e outra, o papel e o
      // mesmo. (E se a impressao falhar, da pra reimprimir depois na aba Pagas do Contas a Receber.)
      const pagos = (titulos ?? []).filter((t) => selTit.has(t.id) && (valoresTit[t.id] ?? 0) > 0.005);
      const agora = new Date();
      imprimirHTML(gerarReciboHTML({
        cliente: recebimento.cliente_conta_nome ?? pagos[0]?.cliente_nome ?? '',
        cliente_cpf_cnpj: pagos[0]?.cliente_cpf_cnpj ?? null,
        formaPagamento: 'Pix',
        operador: usuario?.nome ?? '',
        data: agora.toLocaleDateString('pt-BR'),
        hora: agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        titulos: pagos.map((t) => {
          const original = Number(t.valor_original ?? t.valor);
          const pago = valoresTit[t.id]!;
          return {
            orcamento_numero: t.orcamento_numero,
            nota_tipo: t.nota_tipo ?? null,
            nota_numero: t.nota_numero ?? null,
            data_emissao: t.data_emissao ?? null,
            data_vencimento: t.data_vencimento,
            valor_original: original,
            valor_pago: pago,
            saldo: +(original - pago).toFixed(2),
          };
        }),
        total_recebido: Number(r.total_recebido),
        observacoes: null,
      }));
      onVinculado();
      onClose();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setBaixandoTit(false); }
  }
  const vinculos = recebimento.vinculos ?? [];

  // `retomar` vem preenchido quando estamos repetindo o vínculo após o usuário confirmar o aviso de
  // conta divergente — aí pulamos a pergunta de crediário (já respondida na 1ª passada) e mandamos a
  // flag confirmar_conta_divergente pro backend deixar passar.
  async function vincular(pedido: PedidoPix, retomar?: { confirmarConta: boolean }) {
    // DA-447 — SOBRA NO RECEBIMENTO: este vínculo vai consumir só uma parte do Pix? Pergunta ANTES,
    // nomeando os dois números. Um Pix cobrindo vários pedidos é legítimo e segue com um clique; o
    // que isso pega é o Pix de outro cliente usado pra fechar uma venda pequena (caso real: R$ 14,01
    // quitados com um Pix de R$ 206,00). O aviso NÃO bloqueia — quem morde é o portão do Finalizar,
    // que recusa a venda enquanto sobrar saldo. Por isso o texto diz o que vai acontecer com ela.
    //
    // O QUE O PEDIDO CONSOME é a MESMA conta do backend (calcularAlocacaoPix) e da lista logo
    // abaixo: Pix declarado + parte sem forma + DINHEIRO e CREDIÁRIO conversíveis ([DA-848]), menos
    // o já vinculado. Contar só o Pix
    // declarado fazia todo pedido de fiado (que tem Pix declarado = 0) disparar "consome R$ 0,00 /
    // vão sobrar 100%" — alarme falso em cima do vínculo mais comum, que é justamente o crediário
    // quitado por Pix. Venda finalizada não converte fiado, e nela o campo nem vem (undefined → 0).
    if (!retomar) {
      const dispAgora = Math.max(0, Math.round((dispPix - alocadoLocal) * 100) / 100);
      const faltaPedido = Math.max(0, Math.round(
        ((Number(pedido.valor_pix_declarado) + Number(pedido.valor_crediario ?? 0) + Number(pedido.valor_nao_declarado ?? 0) + Number(pedido.valor_dinheiro ?? 0))
          - Number(pedido.valor_pix_vinculado)) * 100) / 100);
      const vaiAlocar = Math.min(dispAgora, faltaPedido);
      const vaiSobrar = Math.round((dispAgora - vaiAlocar) * 100) / 100;
      if (vaiSobrar > TOLERANCIA_CENTAVO_CALIBRACAO) {
        const ok = await dialog.confirmar(
          `Este Pix é de ${formatMoeda(recebimento.valor)} e o pedido ${pedido.venda_numero ?? pedido.numero} `
          + `(${pedido.cliente_nome}) consome ${formatMoeda(vaiAlocar)}.\n\n`
          + `Vão sobrar ${formatMoeda(vaiSobrar)} sem dono neste recebimento.\n\n`
          + `Se este Pix cobre OUTROS pedidos, confirme e vincule os demais em seguida — as notas de todos saem juntas quando o recebimento fechar. `
          + `Se não cobre, este Pix provavelmente é de outro cliente: procure o recebimento no valor do pedido.\n\n`
          + `Enquanto sobrar saldo neste recebimento, esta venda não pode ser finalizada.`,
          { titulo: 'Vai sobrar dinheiro neste Pix', textoConfirmar: 'Vincular mesmo assim', perigoso: true },
        );
        if (!ok) return;
      }
    }
    if (!retomar && pedido.eh_crediario) {
      const ok = await dialog.confirmar(
        `O pedido ${pedido.numero} (${pedido.cliente_nome}) tem fiado (CREDIÁRIO).\n\n`
        + `Vincular este Pix de ${formatMoeda(recebimento.valor)} marca como PAGO EM PIX a parte do fiado que ele cobre — esse valor deixa de ser cobrado ao finalizar. Se sobrar saldo, continua como fiado.\n\n`
        + `Confira que este Pix é mesmo o pagamento deste cliente antes de confirmar.`,
        { titulo: 'Crediário pago em Pix?', textoConfirmar: 'Sim, marcar como pago em Pix', perigoso: true },
      );
      if (!ok) return;
    }
    setVinculando(pedido.id);
    try {
      const r = await api.post<{ valor_alocado: number }>(
        `/api/mercadopago/recebimentos/${recebimento.id}/vincular`,
        { orcamento_id: pedido.id, confirmar_conversao: pedido.eh_crediario === true, confirmar_conta_divergente: retomar?.confirmarConta === true },
      );
      toast.sucesso(`Alocado ${formatMoeda(r.valor_alocado)}`);
      onVinculado();
      const novoAlocado = alocadoLocal + Number(r.valor_alocado);
      setAlocadoLocal(novoAlocado);
      if (dispPix - novoAlocado <= 0.005) { onClose(); return; }
      recarregar();
    } catch (e: any) {
      // Backend recusou porque a conta do recebimento (Mercado Pago) ≠ a conta de Pix declarada na
      // venda (ex.: Pagbank). Pergunta e, se confirmado, repete o vínculo forçando a flag.
      if (e instanceof ApiError && e.corpo?.erro === 'conta_divergente') {
        setVinculando(null);
        const ok = await dialog.confirmar(
          `Esta venda foi declarada como Pix no ${e.corpo.conta_venda}, mas este recebimento é do ${e.corpo.conta_recebimento}.\n\n`
          + `O dinheiro não pode ter caído no ${e.corpo.conta_recebimento} se a venda foi recebida no ${e.corpo.conta_venda}. Confira antes de prosseguir.`,
          { titulo: 'Conta diferente da declarada', textoConfirmar: 'Vincular mesmo assim', perigoso: true },
        );
        if (ok) await vincular(pedido, { confirmarConta: true });
        return;
      }
      aviso.erro(e.message);
    }
    finally { setVinculando(null); }
  }

  async function removerVinculo(vinculoId: number) {
    setRemovendoId(vinculoId);
    try {
      await api.delete(`/api/mercadopago/vinculos/${vinculoId}`);
      toast.sucesso('Vínculo removido');
      onVinculado();
      recarregar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setRemovendoId(null); }
  }

  async function conferirSemVinculo() {
    setConferindo(true);
    try {
      await api.post(`/api/mercadopago/recebimentos/${recebimento.id}/conferir`, { conferido: true });
      toast.sucesso('Pix conferido sem vínculo');
      onConferido();
      onClose();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setConferindo(false); }
  }

  const titulo = `Vincular Pix ${formatMoeda(recebimento.valor)} — casar com a venda`;

  // Busca livre (só faz sentido na aba Vendas, que agora traz qualquer data): casa número do pedido,
  // número da venda, nome do cliente e os valores (total / falta), digitados com vírgula ou ponto.
  const termo = busca.trim().toLowerCase().replace(',', '.');
  const linhas = (pedidos ?? []).map((p) => {
    const aberto = Number(p.valor_pix_declarado) + Number(p.valor_crediario ?? 0) + Number(p.valor_nao_declarado ?? 0) + Number(p.valor_dinheiro ?? 0);
    const falta = Math.max(0, aberto - Number(p.valor_pix_vinculado));
    const mesmoValor = Math.abs(falta - dispPix) <= 0.005;
    return { p, falta, mesmoValor };
  }).filter(({ p, falta }) => {
    if (!termo) return true;
    const alvo = [p.numero, p.venda_numero ?? '', p.cliente_nome ?? '', Number(p.total).toFixed(2), falta.toFixed(2)]
      .join(' ').toLowerCase();
    return alvo.includes(termo);
  }).sort((a, b) => {
    if (a.mesmoValor !== b.mesmoValor) return a.mesmoValor ? -1 : 1;
    // Pedidos no caixa: fila (mais antigo primeiro). Vendas finalizadas: mais recente primeiro — a
    // lista é de qualquer data, e o que se concilia quase sempre é dos últimos dias.
    const asc = a.p.atualizado_em < b.p.atualizado_em ? -1 : 1;
    return aba === 'vendas' ? -asc : asc;
  });

  return (
    <Modal aberto titulo={titulo} onFechar={onClose} largura={820}>
      <div style={{ display: 'flex', gap: 24, padding: '12px 0 16px', borderBottom: '1px solid var(--input-border,#e5e7eb)', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>TOTAL RECEBIDO</div>
          <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{formatMoeda(recebimento.valor)}</div>
        </div>
        {Number(recebimento.valor_devolvido ?? 0) > 0.005 && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>DEVOLVIDO</div>
            <div style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--danger,#dc2626)' }}>−{formatMoeda(Number(recebimento.valor_devolvido))}</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>JÁ ALOCADO</div>
          <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{formatMoeda(Number(recebimento.valor_vinculado ?? 0))}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>DISPONÍVEL</div>
          <div style={{ fontWeight: 700, fontFamily: 'monospace', color: dispPix > 0.005 ? 'var(--text-color)' : 'var(--text-muted)' }}>{formatMoeda(dispPix)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>HORA DO PIX</div>
          <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{formatHoraSP(recebimento.data_pagamento ?? recebimento.criado_em)}</div>
        </div>
        {recebimento.pagador_doc && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>ORIGEM</div>
            <div style={{ fontSize: 12 }}>{recebimento.pagador_doc}</div>
          </div>
        )}
      </div>

      {vinculos.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>VÍNCULOS ATIVOS</div>
          {vinculos.map((v) => (
            <div key={v.vinculo_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--grid-row-border,#f3f4f6)', fontSize: 13 }}>
              <span style={{ flex: 1 }}><strong>{v.numero}</strong> — {v.cliente_nome}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatMoeda(v.valor)}</span>
              {podeDesvincular && (
                <button type="button" title="Remover vínculo" disabled={removendo === v.vinculo_id}
                  onClick={() => void removerVinculo(v.vinculo_id)}
                  style={{ padding: '3px 6px', border: 0, background: 'transparent', color: 'var(--danger,#dc2626)', cursor: 'pointer', borderRadius: 4, display: 'flex', alignItems: 'center' }}>
                  {removendo === v.vinculo_id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {/* Abas: acha a venda esteja ela ainda no caixa (Pedido para Finalizar) ou já finalizada
            (Vendas finalizadas — qualquer data, DA-299). */}
        {/* Desenho de ABA da casa (`aba-btn`, o mesmo das abas da página do Caixa), não de botão. Até
            24/10/2026 a aba ativa era um `btn-primario` azul cheio, e "Pedido para Finalizar" lia como
            COMANDO: o Ruan clicou nela esperando vincular o Pix ao pedido em aberto (ORC-009311) —
            quem vincula é o botão da LINHA do pedido. */}
        <div className="abas" style={{ marginBottom: 10, alignItems: 'center' }}>
          {([['pedidos', 'Pedido para Finalizar'], ['vendas', 'Vendas finalizadas']] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setAba(k)}
              className={`aba-btn ${aba === k ? 'aba-btn-ativo' : ''}`}>
              {label}
            </button>
          ))}
          {/* Fiado: só existe com o pagador identificado — sem saber de quem é o dinheiro, não se baixa
              título de ninguém. É a porta pro mesmo recebimento pagar um CREDIÁRIO em vez de uma venda. */}
          {clienteId && (
            <button type="button" onClick={() => setAba('titulos')}
              className={`aba-btn ${aba === 'titulos' ? 'aba-btn-ativo' : ''}`}>
              Contas a Receber
            </button>
          )}
          {aba === 'vendas' && (
            <input value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por venda, cliente ou valor"
              style={{ marginLeft: 'auto', width: 260, fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--input-border,#e5e7eb)', backgroundColor: 'var(--input-bg, #fff)', color: 'var(--text-color)' }} />
          )}
        </div>
        {aba === 'titulos' ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
              TÍTULOS EM ABERTO DE {(recebimento.cliente_conta_nome ?? '').toUpperCase()}
            </div>
            {titulos === null ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={16} className="animate-spin" style={{ display: 'inline' }} /></div>
            ) : titulos.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Este cliente não tem título em aberto.
              </div>
            ) : (
              <>
                <TitulosParaReceber
                  titulos={titulos}
                  valores={valoresTit}
                  onValores={setValoresTit}
                  selecionados={selTit}
                  onSelecionados={setSelTit}
                  teto={dispPix}
                  rotuloTeto="disponível deste Pix"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button type="button" className="btn-primario"
                    disabled={baixandoTit || totalTitulos(titulos, valoresTit, selTit) > dispPix + 0.005}
                    onClick={() => void receberTitulos()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {baixandoTit ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Receber {formatMoeda(totalTitulos(titulos, valoresTit, selTit))} com este Pix
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
          {aba === 'vendas' ? 'VENDAS FINALIZADAS COM PIX EM ABERTO (QUALQUER DATA)' : 'PEDIDOS COM PIX PENDENTE'}
        </div>
        {pedidos === null ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={16} className="animate-spin" style={{ display: 'inline' }} /></div>
        ) : linhas.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {aba === 'vendas'
              ? (termo ? 'Nenhuma venda bate com a busca.' : 'Nenhuma venda finalizada aguardando Pix.')
              : 'Nenhum pedido aguardando Pix no caixa.'}
          </div>
        ) : (
          <table className="tabela" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{aba === 'vendas' ? 'Venda' : 'Pedido'}</th>
                <th>{aba === 'vendas' ? 'Data' : 'Hora'}</th>
                <th style={{ textAlign: 'right' }}>Pix declarado</th>
                <th style={{ textAlign: 'right' }}>Falta</th>
                <th style={{ textAlign: 'right', width: 170 }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(({ p, falta, mesmoValor }) => {
                const jaVinculado = vinculos.some((v) => v.orcamento_id === p.id);
                return (
                  <tr key={p.id} style={mesmoValor ? { backgroundColor: '#fde68a', color: '#000' } : undefined}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {p.venda_numero ?? p.numero}
                        {p.eh_crediario && (
                          <span style={{ backgroundColor: '#fde68a', color: '#000', fontWeight: 700, padding: '1px 8px', borderRadius: 999, fontSize: 11, border: '1px solid #d97706' }}>Crediário</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: mesmoValor ? '#000' : 'var(--text-muted)' }}>
                        <strong style={{ color: 'var(--primary)' }}>{p.cliente_nome}</strong>
                        {aba === 'vendas' && <> · total {formatMoeda(p.total)}</>}
                      </div>
                    </td>
                    <td className="cel-num" style={{ whiteSpace: 'nowrap' }}>
                      {aba === 'vendas'
                        ? formatDataHoraSP(p.finalizada_em ?? p.atualizado_em)
                        : formatHoraSP(p.atualizado_em)}
                    </td>
                    <td className="cel-num" style={{ textAlign: 'right' }}>
                      {p.eh_crediario
                        ? <span style={{ fontSize: 12, color: mesmoValor ? '#000' : 'var(--text-muted)' }}>{formatMoeda(p.valor_crediario ?? 0)} (Crediário)</span>
                        : Number(p.valor_pix_declarado) <= 0.005 && Number(p.valor_dinheiro ?? 0) > 0.005
                          // Pedido em DINHEIRO: vincular troca a forma pra Pix (decisao do Ruan, 24/10/2026).
                          ? <span style={{ fontSize: 12, color: mesmoValor ? '#000' : 'var(--text-muted)' }}>{formatMoeda(p.valor_dinheiro ?? 0)} (Dinheiro)</span>
                        : Number(p.valor_pix_declarado) <= 0.005 && Number(p.valor_nao_declarado ?? 0) > 0.005
                          // Pedido sem forma declarada (ex.: devolvido por venda cancelada): "R$ 0,00"
                          // leria como "nada a receber" ao lado de uma Falta cheia.
                          ? <span style={{ fontSize: 12, color: mesmoValor ? '#000' : 'var(--text-muted)' }}>sem forma marcada</span>
                          : formatMoeda(p.valor_pix_declarado)}
                    </td>
                    <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatMoeda(falta)}
                      {mesmoValor && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700 }}>= valor do Pix</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {dispPix <= 0.005 ? (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Pix sem saldo</span>
                      ) : (
                        <button type="button" className={jaVinculado ? 'btn-secundario' : 'btn-primario'}
                          style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          disabled={vinculando === p.id} onClick={() => void vincular(p)}>
                          {vinculando === p.id ? <Loader2 size={13} className="animate-spin" /> : <Link size={13} />}
                          {jaVinculado ? 'Complementar' : 'Vincular'} {formatMoeda(Math.min(dispPix, falta))}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
          </>
        )}
      </div>

      {/* Saída pra Pix sem venda (adiantamento, erro do cliente) — disponível em qualquer aba, não só no Conferir. */}
      {dispPix > 0.005 && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--input-border,#e5e7eb)' }}>
          {!confirmandoSemVinculo ? (
            <button type="button" className="btn-secundario" style={{ fontSize: 12 }} onClick={() => setConfirmandoSemVinculo(true)}>
              Não há venda correspondente — conferir sem vincular
            </button>
          ) : (
            <div style={{ backgroundColor: '#fde68a', color: '#000', padding: '10px 12px', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Conferir SEM vincular a uma venda? Fica registrado quem e quando, mas sem rastro de qual venda — use só pra Pix sem venda (adiantamento, erro do cliente).
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn-primario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  disabled={conferindo} onClick={() => void conferirSemVinculo()}>
                  {conferindo ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  Confirmar conferência sem venda
                </button>
                <button type="button" className="btn-secundario" style={{ fontSize: 12 }} onClick={() => setConfirmandoSemVinculo(false)}>Cancelar</button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---------- Modal Conferir "Desconhecido" (informa onde o dinheiro caiu) ----------
export function ConferirDesconhecidoModal({ alvo, contas, onClose, onConferido }: {
  alvo: Desconhecido;
  contas: ContaBanco[];
  onClose: () => void;
  onConferido: () => void;
}) {
  const toast = useToast();
  const aviso = useAviso();
  const [caixaId, setCaixaId] = useState<number | ''>('');
  const [agencia, setAgencia] = useState('');
  const [conta, setConta] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function confirmar() {
    if (!caixaId) { aviso.erro('Selecione o banco onde o dinheiro caiu'); return; }
    const banco = contas.find((c) => c.id === caixaId)?.nome ?? '';
    setSalvando(true);
    try {
      await api.post('/api/conciliacao/conferir-desconhecido', {
        orcamento_id: alvo.orcamento_id,
        forma: alvo.forma,
        banco,
        caixa_id: caixaId,
        agencia: agencia.trim() || null,
        conta: conta.trim() || null,
      });
      toast.sucesso(`Conciliado em ${banco}`);
      onConferido();
      onClose();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setSalvando(false); }
  }

  return (
    <Modal aberto titulo={`Conferir ${alvo.forma === 'pix' ? 'Pix' : 'Cartão'} — ${alvo.numero}`} onFechar={onClose} largura={520}
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn-secundario" onClick={onClose} disabled={salvando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={() => void confirmar()} disabled={salvando}>
            {salvando ? <Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} /> : 'Confirmar conciliação'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 14, fontSize: 13 }}>
        <div style={{ backgroundColor: '#fde68a', color: '#000', borderRadius: 8, padding: '10px 12px', lineHeight: 1.5 }}>
          A venda <strong>{alvo.numero}</strong> ({alvo.cliente_nome}) está finalizada com{' '}
          <strong>{alvo.forma === 'pix' ? 'Pix' : 'cartão'} de {formatMoeda(alvo.valor)}</strong>, mas o sistema não capturou
          esse recebimento. Informe <strong>onde o dinheiro caiu</strong> pra concluir a conciliação.
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Banco / Conta <span style={{ color: 'var(--danger,#dc2626)' }}>*</span></label>
          <select value={caixaId} onChange={(e) => setCaixaId(e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, fontSize: 13 }}>
            <option value="">Selecione...</option>
            {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            A conta escolhida passa a valer pra venda — corrige a conta da venda e o fechamento de caixa.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Agência (opcional)</label>
            <input value={agencia} onChange={(e) => setAgencia(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, fontSize: 13 }} placeholder="Ex: 1234" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Conta (opcional)</label>
            <input value={conta} onChange={(e) => setConta(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, fontSize: 13 }} placeholder="Ex: 56789-0" />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// `onIrAoPedido` leva pra aba "Pedidos pra finalizar" — a ponte da linha "Sem venda", cuja resolucao
// mora la (finalizar o pedido), nao aqui. Opcional: sem ela a linha so informa.
export function ConciliacaoBancariaTab({ podeVincular, podeConferir, onIrAoPedido }: { podeVincular: boolean; podeConferir: boolean; onIrAoPedido?: () => void }) {
  const { socket } = useSocket();
  const toast = useToast();
  const aviso = useAviso();
  const dialog = useDialog();
  const [segmento, setSegmento] = useState<Segmento>('todos');
  const [dia, setDia] = useState(hojeISO());
  const [mostrarConciliados, setMostrarConciliados] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  const [pix, setPix] = useState<RecebimentoPix[]>([]);
  const [cartao, setCartao] = useState<RecebimentoCartao[]>([]);
  const [desconhecidos, setDesconhecidos] = useState<Desconhecido[]>([]);
  const [contasBanco, setContasBanco] = useState<ContaBanco[]>([]);

  const [modalPix, setModalPix] = useState<{ r: RecebimentoPix; modo: 'vincular' | 'conferir' } | null>(null);
  const [identificarConta, setIdentificarConta] = useState<RecebimentoPix | null>(null);
  // Devolucao de Pix (DA-506): o recebimento em que o operador clicou "Devolver", e o desfecho
  // (protocolo + comprovante) que fica na tela ate ele fechar.
  const [estornarPix, setEstornarPix] = useState<RecebimentoPix | null>(null);
  const [devolucaoFeita, setDevolucaoFeita] = useState<EstornoRealizado | null>(null);
  const [modalDesc, setModalDesc] = useState<Desconhecido | null>(null);
  const [conferindoCartao, setConferindoCartao] = useState<number | null>(null);
  const [desconferindoCartao, setDesconferindoCartao] = useState<number | null>(null);
  const [reconciliandoCartao, setReconciliandoCartao] = useState<number | null>(null);
  const [comprovanteEstorno, setComprovanteEstorno] = useState<number | null>(null);
  const [auditorAberto, setAuditorAberto] = useState(false);
  // Estornos pendentes (DA-280): cartão de venda cancelada aguardando decisão (estornar OU reaproveitar).
  const [estornosPendentes, setEstornosPendentes] = useState<EstornoPendente[]>([]);
  const [estornarAlvo, setEstornarAlvo] = useState<EstornoPendente | null>(null);
  const [estornandoId, setEstornandoId] = useState<number | null>(null);

  const filtrosRef = useRef({ dia, mostrarConciliados });
  filtrosRef.current = { dia, mostrarConciliados };

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [pixRows, cartaoRows, descRows] = await Promise.all([
        api.get<RecebimentoPix[]>('/api/mercadopago/recebimentos', { query: { dia, conferidos: mostrarConciliados } }),
        api.get<RecebimentoCartao[]>('/api/cartao/recebimentos', { query: { dia } }),
        api.get<Desconhecido[]>('/api/conciliacao/desconhecidos'),
      ]);
      setPix(pixRows);
      setCartao(cartaoRows);
      setDesconhecidos(descRows);
    } catch (e: any) { aviso.erro(e.message); }
    finally { setLoading(false); }
  }, [dia, mostrarConciliados, toast]);

  useEffect(() => { void carregar(); }, [carregar]);
  useEffect(() => {
    api.get<ContaBanco[]>('/api/conciliacao/contas-banco').then(setContasBanco).catch(() => setContasBanco([]));
  }, []);

  // Estornos pendentes NÃO dependem do dia (persistem até resolver, como o aviso de pendência de
  // todas as datas). Só admin vê/age (podeConferir === admin nesta aba).
  const carregarEstornos = useCallback(async () => {
    if (!podeConferir) { setEstornosPendentes([]); return; }
    try {
      const rows = await api.get<EstornoPendente[]>('/api/cartao/estornos-pendentes');
      setEstornosPendentes(rows);
    } catch { setEstornosPendentes([]); }
  }, [podeConferir]);
  useEffect(() => { void carregarEstornos(); }, [carregarEstornos]);

  useEffect(() => {
    if (!socket) return;
    const onNovo = (p: { registro?: RecebimentoPix; novo?: boolean }) => {
      if (p.novo && p.registro?.status === 'approved') { try { tocarBeep(); } catch { /* autoplay */ } }
      if (!filtrosRef.current.mostrarConciliados || filtrosRef.current.dia === hojeISO()) void carregar();
    };
    const onAtualizado = () => { void carregar(); };
    const onEstornoPendente = () => { void carregarEstornos(); };
    socket.on('mp:pix_recebido', onNovo);
    socket.on('mp:pix_atualizado', onAtualizado);
    socket.on('cartao:aprovado', onAtualizado);
    socket.on('cartao:estorno_pendente', onEstornoPendente);
    return () => {
      socket.off('mp:pix_recebido', onNovo);
      socket.off('mp:pix_atualizado', onAtualizado);
      socket.off('cartao:aprovado', onAtualizado);
      socket.off('cartao:estorno_pendente', onEstornoPendente);
    };
  }, [socket, carregar, carregarEstornos]);

  async function sincronizar() {
    setSincronizando(true);
    try {
      const [pixSync] = await Promise.all([
        api.post<{ novos: number }>('/api/mercadopago/sync', {}).catch(() => ({ novos: 0 })),
        podeConferir ? api.post('/api/cartao/backfill-liquido', {}).catch(() => null) : Promise.resolve(null),
      ]);
      toast.sucesso(pixSync.novos > 0 ? `${pixSync.novos} novo(s) Pix` : 'Sincronizado — nada novo');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setSincronizando(false); }
  }

  async function vincularConta(cliente: { id: number; nome: string }) {
    const rec = identificarConta;
    if (!rec) return;
    setIdentificarConta(null);
    try {
      await api.post('/api/mercadopago/contas-pix/vincular', { mp_recebimento_id: rec.id, cliente_id: cliente.id });
      toast.sucesso(`Conta identificada como ${cliente.nome}`);
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
  }

  async function desvincularConta(r: RecebimentoPix) {
    if (!r.cliente_conta_vinculo_id) return;
    // O vinculo mora na CONTA (cliente_contas_pix), nao neste Pix — entao a confirmacao diz o alcance:
    // o nome sai de todo Pix daquela conta, passado e futuro. Sem isso o operador acha que esta
    // corrigindo so a linha que esta olhando (DA-272).
    const ok = await dialog.confirmar(
      `Tirar ${r.cliente_conta_nome} da conta ${r.pagador_doc ?? 'do pagador'}? O nome deixa de aparecer em TODOS os Pix dessa conta — os já recebidos e os próximos.`,
      { perigoso: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/api/mercadopago/contas-pix/${r.cliente_conta_vinculo_id}`);
      toast.sucesso('Conta desvinculada');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
  }

  async function conferirCartao(r: RecebimentoCartao) {
    if (r.valor_liquido == null) { aviso.erro('Líquido não disponível — clique "Atualizar" primeiro.'); return; }
    setConferindoCartao(r.id);
    try {
      await api.post(`/api/cartao/cobrancas/${r.id}/conferir`, {});
      toast.sucesso('Conferido — CP da taxa criado.');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setConferindoCartao(null); }
  }

  // Desfaz a conciliação de um cartão (recebimento devolvido/estornado ou conciliado por engano).
  // Backend cancela o CP da taxa + marca 'desconciliado' (não reconcilia sozinho). Bloqueia se a
  // venda tem NF aprovada — aí o caminho é cancelar a venda (a mensagem do backend orienta).
  async function desconferirCartao(r: RecebimentoCartao) {
    const ok = await dialog.confirmar(
      `Desconciliar o cartão de ${formatMoeda(Number(r.valor_pago ?? r.valor))} da venda ${r.venda_numero ?? r.numero}?\n\n`
      + `Desfaz a conciliação e cancela o lançamento da taxa em Contas a Pagar. Use quando o recebimento foi devolvido/estornado ou conciliado por engano.\n\n`
      + `A linha passa a aparecer como Cancelado e não volta a conciliar sozinha.`,
      { titulo: 'Desconciliar recebimento?', textoConfirmar: 'Sim, desconciliar', perigoso: true },
    );
    if (!ok) return;
    setDesconferindoCartao(r.id);
    try {
      await api.post(`/api/cartao/cobrancas/${r.id}/desconferir`, {});
      toast.sucesso('Recebimento desconciliado');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setDesconferindoCartao(null); }
  }

  // REVINCULAR — a volta do desconciliar ([DA-720]). Sem ela a linha Cancelado fica SEM nenhum
  // botão (a condição dos dois é `!desconciliado`), e desfazer vira porta de mão única: o conserto
  // só saía por SQL. Volta como "Recebido", não como conciliada — quem lança a taxa é o Conferir,
  // que fica ao lado e continua sendo um gesto consciente.
  async function reconciliarCartao(r: RecebimentoCartao) {
    const ok = await dialog.confirmar(
      `Revincular o cartão de ${formatMoeda(Number(r.valor_pago ?? r.valor))} ${r.venda_numero ? `da venda ${r.venda_numero}` : `do pedido ${r.numero}`}?\n\n`
      + `A linha volta a aparecer como Recebido. Depois é só Conferir pra lançar a taxa de novo.`,
      { titulo: 'Revincular recebimento?', textoConfirmar: 'Sim, revincular' },
    );
    if (!ok) return;
    setReconciliandoCartao(r.id);
    try {
      const resp = await api.post<{ mensagem: string }>(`/api/cartao/cobrancas/${r.id}/reconciliar`, {});
      toast.sucesso(resp.mensagem ?? 'Recebimento revinculado');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setReconciliandoCartao(null); }
  }

  // ESTORNO MANUAL (DA-280): devolve o valor ao cartão do cliente no MP. Ação separada, só-admin,
  // IRREVERSÍVEL. Passo 1: aviso forte (dá pra desistir). Passo 2: senha gerencial. Nunca é efeito
  // colateral de cancelar — foi o que causou o incidente que originou esta mudança.
  // `semVenda`: cartão pago de pedido que nunca virou venda (12/10/2026) — mesma porta, mesma senha;
  // muda só o conselho final, porque ali "não estorne" significa finalizar o pedido, não refazer a venda.
  async function pedirEstorno(e: EstornoPendente, semVenda = false) {
    const ok = await dialog.confirmar(
      `Devolver ${formatMoeda(Number(e.valor_pago ?? e.valor))} ao cartão de ${e.cliente_nome} (${semVenda ? 'pedido' : 'venda'} ${e.venda_numero ?? e.orcamento_numero ?? '—'})?\n\n`
      + `O dinheiro sai da conta da loja e volta pro cartão do cliente no Mercado Pago. Essa devolução é `
      + `IRREVERSÍVEL — não dá pra recobrar depois. Só faça se for realmente pra devolver; `
      + (semVenda
        ? `se o cliente vai levar a mercadoria, finalize o pedido no caixa (não estorne).`
        : `se for refazer a venda, o pagamento é reaproveitado (não estorne).`),
      { titulo: 'Estornar no cartão?', textoConfirmar: 'Sim, quero devolver', perigoso: true },
    );
    if (ok) setEstornarAlvo(e);
  }
  async function confirmarEstorno(senha?: string) {
    const e = estornarAlvo;
    setEstornarAlvo(null);
    if (!e) return;
    setEstornandoId(e.id);
    try {
      const r = await api.post<{ valor: number }>(`/api/cartao/cobrancas/${e.id}/estornar`, { senha_gerencial: senha });
      toast.sucesso(`Estorno de ${formatMoeda(Number(r.valor ?? e.valor))} enviado ao cartão do cliente`);
      void carregarEstornos();
      void carregar();
    } catch (err: any) { aviso.erro(err.message); }
    finally { setEstornandoId(null); }
  }

  // Desfaz a conciliação do Pix POR INTEIRO: tira o selo E remove os vínculos. Até 2026-07-30 este
  // botão só desconferia, e o vínculo (que é o que segura o dinheiro) ficava de pé — o Pix parecia
  // livre na tela e continuava alocado. A confirmação LISTA o que vai sair porque um Pix cobre
  // vários pedidos (há recebimento amarrado em 15 vendas). Recusa do backend (NF autorizada, título
  // de CR) vem em modal via aviso.erro, não em toast: é dinheiro.
  async function desfazerPix(r: RecebimentoPix) {
    const vs = r.vinculos ?? [];
    const liquido = Number(r.valor) - Number(r.valor_devolvido ?? 0);
    const lista = vs.map((v) => `• ${v.venda_numero ?? v.numero} — ${formatMoeda(Number(v.valor))}`).join('\n');
    const ok = await dialog.confirmar(
      vs.length === 0
        ? `Tirar a marca de conferido do Pix de ${formatMoeda(liquido)}?\n\n`
          + `Ele volta pra lista de recebimentos a conciliar.`
        : `Desfazer a conciliação do Pix de ${formatMoeda(liquido)}?\n\n`
          + `Isso DESVINCULA ${vs.length === 1 ? 'o pedido abaixo' : `os ${vs.length} pedidos abaixo`}:\n${lista}\n\n`
          + `O Pix volta livre pra ser vinculado onde for certo, e ${vs.length === 1 ? 'o pedido volta' : 'os pedidos voltam'} a aparecer como aguardando Pix.\n\n`
          + `Atenção: se algum desses vínculos converteu crediário em Pix, desfazer NÃO devolve o fiado — confira a forma de pagamento do pedido depois.`,
      { titulo: 'Desfazer conciliação?', textoConfirmar: 'Sim, desfazer', perigoso: true },
    );
    if (!ok) return;
    try {
      await api.post(`/api/mercadopago/recebimentos/${r.id}/desfazer`, {});
      toast.sucesso(vs.length > 0 ? `Conciliação desfeita — ${vs.length} vínculo(s) removido(s)` : 'Conciliação desfeita');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
  }

  // [DA-549] Desvincula o Pix da transferência que o explicava — e a porta fica NA LINHA, não só no
  // modal onde o vínculo nasceu ([DA-506]: ação que só existe no modal de desfecho tem vida útil de
  // uma tela).
  //
  // O texto diz, antes de confirmar, o que ele NÃO faz: a transferência continua de pé. O dinheiro
  // andou de verdade nas duas contas, e mexer em saldo é outra ação com outra porta — botão cujo
  // rótulo promete a ação inteira e executa metade é o pecado da [DA-402].
  async function desvincularTransferencia(r: RecebimentoPix) {
    const ok = await dialog.confirmar(
      `Desvincular o Pix de ${formatMoeda(Number(r.valor))} da transferência #${r.transferencia_id}?\n\n`
      + `Ele volta pra fila de recebimentos a conciliar, como se ninguém o tivesse classificado.\n\n`
      + `A TRANSFERÊNCIA CONTINUA DE PÉ — o dinheiro já saiu de ${r.transferencia_origem_nome ?? 'uma conta'} `
      + `e entrou em ${r.transferencia_destino_nome ?? 'outra'}, e isso não se desfaz por aqui. `
      + `Se a transferência é que está errada, corrija pelo Caixa → Transferências.`,
      { titulo: 'Desvincular transferência?', textoConfirmar: 'Sim, desvincular', perigoso: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/api/mercadopago/recebimentos/${r.id}/transferencia`);
      toast.sucesso('Pix desvinculado — voltou pra fila da Conciliação');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
  }

  // "Vincular" na linha Desconhecido (venda finalizada sem recebimento casado): abre O MESMO modal
  // do botão Vincular dos Pix capturados (VincularModal), que é ancorado num Pix recebido. Como a
  // venda já é conhecida, abre direto na aba "Vendas finalizadas" (modo 'conferir'). Escolhe o Pix solto de
  // mesmo valor; senão o mais recente. Sem Pix solto não há o que casar — avisa pra usar o Conferir.
  function abrirVincularDesconhecido(d: Desconhecido) {
    const soltos = pix
      .filter((r) => r.tipo !== 'devolucao' && r.status === 'approved' && valorDisponivelPix(r) > 0.005)
      .sort((a, b) => {
        const am = Math.abs(valorDisponivelPix(a) - Number(d.valor)) <= 0.005 ? 0 : 1;
        const bm = Math.abs(valorDisponivelPix(b) - Number(d.valor)) <= 0.005 ? 0 : 1;
        if (am !== bm) return am - bm;
        return (parseSPDate(b.data_pagamento ?? b.criado_em)?.getTime() ?? 0)
             - (parseSPDate(a.data_pagamento ?? a.criado_em)?.getTime() ?? 0);
      });
    const escolhido = soltos[0];
    if (!escolhido) {
      aviso.erro('Nenhum Pix recebido solto pra casar com esta venda. Se caiu em outro banco, use o Conferir.');
      return;
    }
    setModalPix({ r: escolhido, modo: 'conferir' });
  }

  // Monta a lista unificada respeitando o segmento + "mostrar conciliados".
  type Row =
    | { kind: 'pix'; r: RecebimentoPix }
    | { kind: 'cartao'; r: RecebimentoCartao }
    | { kind: 'desc'; r: Desconhecido };

  const verSeg = (n: 'pix' | 'cartao') => segmento === 'todos' || segmento === n;

  const rows: Row[] = [];
  if (verSeg('pix')) {
    for (const r of pix) {
      // devolução é detalhe do depósito — sempre acompanha
      if (r.tipo === 'devolucao') { rows.push({ kind: 'pix', r }); continue; }
      if (!mostrarConciliados && r.conferido) continue;
      rows.push({ kind: 'pix', r });
    }
  }
  if (verSeg('cartao')) {
    for (const r of cartao) {
      // Cartão (crédito ou débito) capturado SEM venda vinculada aparece SEMPRE, mesmo conferido.
      // "Conferido" só diz que a taxa foi lançada — o vigia do DA-238 concilia no mesmo segundo da
      // aprovação, sem exigir que a venda exista. Se o pedido não for finalizado (voltou pro
      // vendedor, trocou item, valor mudou), o dinheiro real ficava conciliado contra uma venda que
      // nunca nasceu e a linha sumia da tela: recurso invisível não existe (princípio #1).
      const semVenda = !r.venda_numero && r.status === 'pago';
      // Conciliado E desconciliado (Cancelado) são "resolvidos" — ocultos por padrão.
      if (!mostrarConciliados && !semVenda && (r.conferido || r.status === 'desconciliado')) continue;
      rows.push({ kind: 'cartao', r });
    }
  }
  // Desconhecidos nunca estão conciliados (a query só devolve o que falta) — sempre aparecem.
  for (const d of desconhecidos) {
    if (!verSeg(d.forma)) continue;
    rows.push({ kind: 'desc', r: d });
  }

  // Ordena por momento desc (capturado: hora do recebimento; desconhecido: finalização).
  const tsDe = (row: Row): number => {
    if (row.kind === 'pix') return parseSPDate(row.r.data_pagamento ?? row.r.criado_em)?.getTime() ?? 0;
    if (row.kind === 'cartao') return parseSPDate(row.r.pago_em)?.getTime() ?? 0;
    return parseSPDate(row.r.finalizada_em ?? row.r.atualizado_em)?.getTime() ?? 0;
  };
  rows.sort((a, b) => tsDe(b) - tsDe(a));

  const totalPendentes =
    pix.filter((r) => r.tipo !== 'devolucao' && r.status === 'approved' && !r.conferido).length
    // Cartão sem venda conta como pendente mesmo conferido — é dinheiro parado esperando decisão.
    + cartao.filter((r) => (!r.conferido || !r.venda_numero) && r.status !== 'desconciliado').length
    + desconhecidos.length;

  return (
    <div>
      {/* ESTORNOS PENDENTES (DA-280): cartão de venda cancelada — dinheiro retido na loja, aguardando
          decisão do admin. Fundo sólido + letra preta (padrão de alerta, daltonismo-safe). */}
      {podeConferir && estornosPendentes.length > 0 && (
        <div style={{ backgroundColor: '#f97316', color: '#000', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} /> {estornosPendentes.length} cartão(ões) de venda cancelada aguardando decisão de estorno
          </div>
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            O dinheiro ainda está <strong>retido na loja</strong> (o cliente segue cobrado). Clique em <strong>Estornar no cartão</strong> só pra devolver de fato ao cliente — é irreversível. Pra refazer a venda, o pagamento é reaproveitado (não estorne).
          </div>
          <table className="tabela" style={{ width: '100%', background: 'transparent' }}>
            <thead>
              <tr>
                <th>Cancelada em</th>
                <th>Venda</th>
                <th>Cliente</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th>Cartão</th>
                <th style={{ textAlign: 'right', width: 200 }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {estornosPendentes.map((e) => (
                <tr key={`ap${e.id}`}>
                  <td style={{ fontSize: 12 }}>{formatDataHoraSP(e.atualizado_em)}</td>
                  <td style={{ fontSize: 12, fontWeight: 700 }}>{e.venda_numero ?? e.orcamento_numero ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{e.cliente_nome}</td>
                  <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(Number(e.valor_pago ?? e.valor))}</td>
                  <td style={{ fontSize: 12 }}>{e.card_bandeira ?? '—'}{e.card_parcelas && e.card_parcelas > 1 ? ` ${e.card_parcelas}x` : ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="btn-primario" style={{ fontSize: 12, background: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      disabled={estornandoId === e.id} onClick={() => void pedirEstorno(e)}
                      title="Devolver o valor ao cartão do cliente (irreversível, exige senha gerencial)">
                      {estornandoId === e.id ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />} Estornar no cartão
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {/* Filtro segmentado Todos / Pix / Cartão */}
        <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--input-border,#d1d5db)' }}>
          {(['todos', 'pix', 'cartao'] as Segmento[]).map((s) => (
            <button key={s} type="button" onClick={() => setSegmento(s)}
              style={{
                padding: '6px 14px', fontSize: 13, fontWeight: 700, border: 0, cursor: 'pointer',
                background: segmento === s ? '#fde68a' : 'transparent',
                color: segmento === s ? '#000' : 'var(--text-color)',
              }}>
              {s === 'todos' ? 'Todos' : s === 'pix' ? 'Pix' : 'Cartão'}
            </button>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Dia
          <input type="date" value={dia} onChange={(e) => setDia(e.target.value)} style={{ width: 160 }} />
        </label>
        <button type="button" className="btn-secundario" onClick={() => setDia(hojeISO())}>Hoje</button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={mostrarConciliados} onChange={(e) => setMostrarConciliados(e.target.checked)} />
          Mostrar conciliados
        </label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {totalPendentes > 0
            ? <strong style={{ color: 'var(--danger,#dc2626)' }}>{totalPendentes} a conciliar</strong>
            : 'tudo conciliado'}
        </span>
        <button type="button" className="btn-secundario" onClick={sincronizar} disabled={sincronizando} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {sincronizando ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          Atualizar
        </button>
        {podeConferir && (
          <button type="button" className="btn-secundario" onClick={() => setAuditorAberto(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title="Cruzar os pagamentos de cartão do Mercado Pago com o sistema (pega testes feitos fora do sistema)">
            <ShieldCheck size={15} /> Auditar cartão
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          {mostrarConciliados ? 'Nada neste filtro.' : 'Nada a conciliar — tudo conferido.'}
        </div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Hora</th>
              <th>Tipo</th>
              <th style={{ textAlign: 'right' }}>Valor</th>
              {/* Coluna Conta SAIU (pedido dele, 24/10/2026: "aqui nao precisa aparecer, so na
                  Conciliacao Pgto") — dizia sempre "Mercado Pago", e o "Desconhecido" repetia o
                  selo "Sem recebimento" do Status. */}
              <th>Venda / Pagador</th>
              <th>Vendedor</th>
              <th>Status</th>
              <th>Conciliação</th>
              <th style={{ textAlign: 'right', width: 170 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              // ---- Pix capturado ----
              if (row.kind === 'pix') {
                const r = row.r;
                if (r.tipo === 'devolucao') {
                  // A venda da devolucao e' a do Pix estornado: o deposito vem sempre na mesma lista
                  // (o backend so manda a devolucao junto do pai). Sem isto, lembrar de que venda foi
                  // o estorno exigia abrir o Pix de cima e deduzir pelos horarios.
                  const vendasPai = pix.find((p) => p.tipo !== 'devolucao' && p.id === Number(r.mp_recebimento_id))?.vinculos ?? [];
                  return (
                    <tr key={`pd${r.id}`} style={{ backgroundColor: 'var(--devolucao-bg, #fef2f2)' }}>
                      <td style={{ fontSize: 12 }}>{rotuloMomento(r.data_pagamento ?? r.criado_em, dia)}</td>
                      <td style={{ fontSize: 12 }}>Pix</td>
                      <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger,#dc2626)' }}>−{formatMoeda(Number(r.valor))}</td>
                      <td style={{ fontSize: 12 }}>
                        {vendasPai.length > 0 && <div><LinksVendas vinculos={vendasPai} /></div>}
                        <div style={{ color: 'var(--text-muted)', ...(vendasPai.length > 0 ? { fontSize: 11, marginTop: 2 } : {}) }}>
                          Estorno do Pix das {formatHoraSP(r.parent_data_pagamento ?? r.data_pagamento ?? r.criado_em)}
                          {r.cliente_nome && <span style={{ fontWeight: 700, color: 'var(--primary)', marginLeft: 6 }}>· {r.cliente_nome}</span>}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</td>
                      <td>{pillDevolucao()}</td>
                      {/* Quem mandou devolver. 'externa' = feita no app do MP (o MP nao diz quem
                          apertou la), 'sistema' = pela tela, e ai o autor existe (DA-506). */}
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {r.origem === 'sistema' ? <span>✓ {r.autor_nome ?? 'Sistema'}</span> : <span title="Devolucao feita pelo aplicativo do Mercado Pago">app do MP</span>}
                      </td>
                      {/* Sem este botao a devolucao virava linha morta: o protocolo do MP so existia
                          no modal do desfecho, entao cliente que pedisse a prova no dia seguinte
                          ficava sem, e um envio que falhasse nao tinha segunda chance. */}
                      <td style={{ textAlign: 'right' }}>
                        <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => setDevolucaoFeita({
                            devolucao_id: r.id,
                            refund_id: String(r.mp_refund_id ?? ''),
                            valor: Number(r.valor),
                            saldo_restante: Number(r.parent_saldo_restante ?? 0),
                            cliente_id: r.cliente_id ?? null,
                            cliente_nome: r.cliente_nome ?? null,
                          })}
                          title="Ver o comprovante, imprimir ou mandar de novo pelo WhatsApp">
                          <FileText size={13} />Comprovante
                        </button>
                      </td>
                    </tr>
                  );
                }
                const disp = valorDisponivelPix(r);
                const devolvido = Number(r.valor_devolvido ?? 0);
                const vinculos = r.vinculos ?? [];
                // Pix parcialmente vinculado (ja tem vinculo, mas ainda sobra saldo): a hierarquia
                // se inverte — o saldo que FALTA vincular vira o numero em destaque, o total recebido recua.
                const parcial = vinculos.length > 0 && disp > 0.005 && !r.conferido;
                // Com venda na linha, so vale imprimir a linha do pagador se ela DIZ alguma coisa —
                // um "—" solto embaixo do numero da venda seria ruido puro.
                const temPagador = Boolean(
                  r.pagador_nome_exibicao || r.pagador_nome || r.pagador_doc || r.cliente_conta_nome || r.conta_identificavel,
                );
                return (
                  <tr key={`pr${r.id}`} style={r.conferido ? { opacity: 0.6 } : undefined}>
                    <td style={{ fontSize: 12 }}>{rotuloMomento(r.data_pagamento ?? r.criado_em, dia)}</td>
                    <td style={{ fontSize: 12 }}>Pix</td>
                    <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>
                      {parcial ? (
                        <>
                          <div style={{ color: 'var(--primary)' }} title="Saldo deste Pix ainda nao vinculado">{formatMoeda(disp)}</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>de {formatMoeda(Number(r.valor))}</div>
                        </>
                      ) : devolvido > 0.005 ? (
                        // Pix com devolucao: MESMA inversao do parcial acima. Depois de devolver, o
                        // numero que importa e o que SOBROU — e ele que tem que bater com a venda que
                        // vai ser vinculada. O bruto recua pra linha de baixo: continua visivel (o
                        // extrato do MP mostra 222,00 e a conferencia precisa achar esse numero), mas
                        // para de disputar a atencao com o valor que esta em uso.
                        <>
                          <div style={{ color: r.conferido ? undefined : 'var(--primary)' }} title="Saldo deste Pix depois das devolucoes">
                            {formatMoeda(valorLiquidoPix(r))}
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>de {formatMoeda(Number(r.valor))}</div>
                        </>
                      ) : (
                        formatMoeda(Number(r.valor))
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {vinculos.length > 0 && (
                        <div title={vinculos.map((v) => `${v.venda_numero ?? v.numero} ${formatMoeda(v.valor)}`).join(', ')}><LinksVendas vinculos={vinculos} /></div>
                      )}
                      {/* O pagador NAO some quando entra o primeiro vinculo de venda. Esta celula era
                          ou/ou (venda OU pagador) e, com isso, o nome do cliente, o "Desvincular cliente"
                          e o "Identificar" desapareciam pra sempre no primeiro vinculo — inclusive num Pix
                          parcialmente vinculado, que e' justamente quando se quer saber de quem e' o resto
                          (caso real: sobra de R$ 2,99 num Pix de R$ 608,00 ja casado com a VND-001838).
                          Com venda na linha, o pagador recua pra 11px — mesma hierarquia do "de R$ 608,00"
                          da coluna VALOR: o dado principal manda, o de apoio continua legivel. */}
                      {(vinculos.length === 0 || temPagador) && (
                        <div style={vinculos.length > 0 ? { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 } : undefined}>
                          <span style={{ fontWeight: 600 }}>{r.pagador_nome_exibicao || r.pagador_nome || '—'}</span>
                          {r.pagador_doc && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{r.pagador_doc}</span>}
                          {r.cliente_conta_nome ? (
                            <>
                              <span style={{ marginLeft: 6, fontWeight: 700, color: 'var(--primary)' }}>· {r.cliente_conta_nome}</span>
                              {/* Nomeia o OBJETO ("cliente"): na mesma linha existe o "Vincular" da coluna
                                  AÇÕES, que é do pagamento↔venda. Um "Desvincular" solto em cinza 11px
                                  não se distinguia dele, e o Ruan pediu três vezes uma forma de tirar o
                                  cliente que já era este botão. Espelha a forma do "Identificar" ao lado. */}
                              <button type="button" onClick={() => void desvincularConta(r)} title="Tirar este cliente da conta bancária do pagador"
                                style={{ marginLeft: 6, border: 0, background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: vinculos.length > 0 ? 11 : 12, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 3, verticalAlign: 'middle' }}>
                                <UserMinus size={12} /> Desvincular cliente
                              </button>
                            </>
                          ) : r.conta_identificavel ? (
                            <button type="button" onClick={() => setIdentificarConta(r)} title="Vincular esta conta a um cliente cadastrado"
                              style={{ marginLeft: 6, border: 0, background: 'transparent', color: 'var(--primary)', cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3, verticalAlign: 'middle' }}>
                              <UserPlus size={12} /> Identificar
                            </button>
                          ) : null}
                        </div>
                      )}
                      {/* O saldo a vincular agora salta em azul na coluna VALOR — nao repetir aqui em vermelho (vermelho e erro). */}
                    </td>
                    <td style={{ fontSize: 12 }}>{celulaVendedor(Array.from(new Set(vinculos.map((v) => v.vendedor_nome).filter(Boolean))).join(', ') || null)}</td>
                    <td>{r.conferido ? pillConciliado() : pillRecebido()}</td>
                    <td style={{ fontSize: 12 }}>
                      {/* [DA-549] A transferencia manda na leitura desta celula: ela diz O QUE o Pix
                          foi, e nao so quem o conferiu. Escrita na linha, nao em tooltip. */}
                      {r.transferencia_id ? (
                        <span style={{ display: 'inline-block', lineHeight: 1.35 }}>
                          <strong style={{ color: 'var(--primary)' }}>Transferência #{r.transferencia_id}</strong>
                          {r.transferencia_origem_nome && r.transferencia_destino_nome && (
                            <>
                              <br />
                              <span style={{ fontSize: 11, opacity: 0.85 }}>
                                {r.transferencia_origem_nome} → {r.transferencia_destino_nome}
                              </span>
                            </>
                          )}
                        </span>
                      ) : r.conferido ? <span>✓ {r.conferido_por_nome ?? ''}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {r.status === 'approved' && (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          {/* Vincular some quando já conciliado: o Pix está todo alocado e casá-lo de
                              novo geraria inconsistência (NF já emitida). Pra revincular, primeiro Desfazer.
                              [DA-560] MAS o admin continua vendo os "Vínculos" do Pix conciliado — é dali
                              que sai o X que solta UM vínculo isolado, e era o único caminho possível
                              quando o Pix está casado no pedido errado e o "Desfazer" é recusado pela
                              trava fiscal (pedido vizinho com nota autorizada). Sem este botão, a trava
                              do pedido com Pix vinculado viraria beco: a recusa manda desvincular e não
                              havia onde. Ele abre o MESMO modal — que já mostra "DISPONÍVEL R$ 0,00" e
                              recusa vincular de novo pelo backend ('pix_esgotado'). */}
                          {podeVincular && (!r.conferido || (podeConferir && vinculos.length > 0)) && (
                            <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => setModalPix({ r, modo: 'vincular' })}
                              title={r.conferido ? 'Ver e soltar os vínculos deste Pix' : 'Vincular a um pedido do caixa'}>
                              <Link size={13} />{vinculos.length > 0 ? 'Vínculos' : 'Vincular'}
                            </button>
                          )}
                          {/* Devolver: só admin, e só quando o Pix está LIVRE (sem vínculo, sem selo,
                              com saldo). Enquanto houver vínculo, o dinheiro está pago em cima de uma
                              venda — o caminho é Desfazer primeiro. O backend recusa de novo (a trava
                              real mora lá); aqui o botão só não aparece pra não convidar ao erro. */}
                          {podeConferir && !r.conferido && vinculos.length === 0 && valorDisponivelPix(r) > 0.005 && (
                            <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => setEstornarPix(r)} title="Devolver este Pix ao cliente (total ou parcial)">
                              <Undo2 size={13} />Devolver
                            </button>
                          )}
                          {/* [DA-549] Pix amarrado a transferência tem porta PRÓPRIA. O "Desfazer"
                              genérico é recusado pelo backend (409) porque ele limparia o selo e
                              deixaria a FK de pé — e o rótulo dele não anuncia que romperia o laço
                              com uma transferência real. */}
                          {podeConferir && r.transferencia_id ? (
                            <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => void desvincularTransferencia(r)}
                              title="Tirar a amarra com a transferência (a transferência continua de pé)">
                              <Undo2 size={14} />Desvincular transferência
                            </button>
                          ) : podeConferir && (r.conferido ? (
                            <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => void desfazerPix(r)} title="Desfazer a conciliação: tira o selo e desvincula os pedidos">
                              <Undo2 size={14} />Desfazer
                            </button>
                          ) : (
                            <button type="button" className="btn-primario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => setModalPix({ r, modo: 'conferir' })} title="Conferir casando com a venda">
                              <Check size={14} />Conferir
                            </button>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              }
              // ---- Cartão capturado ----
              if (row.kind === 'cartao') {
                const r = row.r;
                const bruto = Number(r.valor_pago ?? r.valor);
                const liquido = r.valor_liquido != null ? Number(r.valor_liquido) : null;
                const taxa = liquido != null ? bruto - liquido : null;
                const desconciliado = r.status === 'desconciliado';
                // Mesma regra do filtro acima: cartão pago cujo pedido não virou venda.
                const semVenda = !r.venda_numero && r.status === 'pago';
                const estadoMp = classificarCartaoMp({
                  statusLocal: r.status ?? 'pago', valor: Number(r.valor), estornoValor: Number(r.estorno_valor ?? 0),
                });
                return (
                  // Sem venda NÃO esmaece: é pendência viva, não linha resolvida.
                  <tr key={`c${r.id}`} style={(r.conferido || desconciliado) && !semVenda ? { opacity: 0.6 } : undefined}>
                    <td style={{ fontSize: 12 }}>{r.pago_em ? rotuloMomento(r.pago_em, dia) : '—'}</td>
                    <td style={{ fontSize: 12 }}>Cartão{r.card_parcelas > 1 ? ` ${r.card_parcelas}x` : ''}</td>
                    <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatMoeda(bruto)}
                      {liquido != null && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>líquido {formatMoeda(liquido)}{taxa != null && taxa > 0.005 ? ` · taxa ${formatMoeda(taxa)}` : ''}</div>}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {/* Sem venda o rótulo cai no pedido (ORC) — avisa que é pedido, não venda. */}
                      <strong>{r.venda_numero ?? r.numero}</strong> {r.cliente_nome}
                      {semVenda && <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>pedido aguardando no caixa</div>}
                      {r.card_bandeira && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.card_bandeira}{r.card_cod_autoriz ? ` · Aut. ${r.card_cod_autoriz}` : ''}</div>}
                    </td>
                    <td style={{ fontSize: 12 }}>{celulaVendedor(r.vendedor_nome)}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {desconciliado ? pillCancelado() : semVenda ? pillSemVenda() : r.conferido ? pillConciliado() : pillRecebido()}
                        {/* Só no parcelado: no 1x não existe juros pra ninguém bancar (DA-516). */}
                        {r.card_parcelas > 1 && r.parcelamento_custo && pillParcelamento(r.parcelamento_custo)}
                        <SeloEstorno estado={estadoMp} />
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {desconciliado
                        ? <span style={{ color: 'var(--text-muted)' }}>desconciliado</span>
                        : semVenda
                          // A taxa pode já estar lançada (o vigia conferiu na aprovação) — o que falta
                          // é a VENDA. Diz as duas coisas pra ninguém achar que é erro de taxa.
                          ? (
                            <span>
                              <strong>pedido não finalizado</strong>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                {r.conferido ? `taxa lançada${r.cp_taxa_id ? ` · CP #${r.cp_taxa_id}` : ''}` : 'taxa não lançada'}
                              </div>
                            </span>
                          )
                          : r.conferido
                            ? <span>✓ {r.conferido_por_nome ?? ''}{r.cp_taxa_id ? ` · CP #${r.cp_taxa_id}` : ''}</span>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        {/* A resolução do "Sem venda" é finalizar o pedido — a ponte fica na linha. */}
                        {semVenda && onIrAoPedido && (
                          <button type="button" className="btn-primario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onClick={onIrAoPedido} title="Abrir a aba de pedidos pra finalizar este pedido">
                            Ir ao pedido
                          </button>
                        )}
                        {/* Estorno do cartão de pedido NÃO finalizado — a porta que o Pix já tinha (DA-506).
                            Só total, só aqui na Conciliação (decisão dele, 12/10/2026). */}
                        {semVenda && podeConferir && r.mp_payment_id && (
                          <button type="button" className="btn-perigo" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            disabled={estornandoId === r.id}
                            onClick={() => void pedirEstorno({
                              id: r.id, orcamento_id: null, valor: r.valor, valor_pago: r.valor_pago,
                              card_bandeira: r.card_bandeira, card_parcelas: r.card_parcelas, mp_payment_id: r.mp_payment_id,
                              pago_em: r.pago_em, atualizado_em: null, orcamento_numero: r.numero, venda_numero: null,
                              cliente_nome: r.cliente_nome,
                            }, true)}
                            title="Devolver o valor TOTAL ao cartão do cliente no Mercado Pago (irreversível)">
                            {estornandoId === r.id ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}Estornar no cartão
                          </button>
                        )}
                        {podeConferir && r.mp_payment_id && (
                          <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onClick={() => setComprovanteEstorno(r.id)} title="Ver o estado real no MP e o comprovante de estorno">
                            <FileText size={13} />Comprovante
                          </button>
                        )}
                        {/* A linha Cancelado ficava SEM ação nenhuma — desfazer era porta de mão
                            única e o conserto dependia de SQL. Agora ela tem a volta. */}
                        {podeConferir && desconciliado && (
                          <button type="button" className="btn-primario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            disabled={reconciliandoCartao === r.id} onClick={() => void reconciliarCartao(r)}
                            title="Voltar este recebimento pra conciliação (volta como Recebido; a taxa se lança no Conferir)">
                            {reconciliandoCartao === r.id ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={14} />}Revincular
                          </button>
                        )}
                        {podeConferir && !desconciliado && (r.conferido ? (
                          <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            disabled={desconferindoCartao === r.id} onClick={() => void desconferirCartao(r)}
                            title="Desfazer a conciliação (recebimento devolvido/estornado ou conciliado por engano)">
                            {desconferindoCartao === r.id ? <Loader2 size={13} className="animate-spin" /> : <X size={14} />}Desfazer
                          </button>
                        ) : (
                          <button type="button" className="btn-primario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            disabled={conferindoCartao === r.id} onClick={() => void conferirCartao(r)}
                            title={liquido == null ? 'Atualize o líquido primeiro' : `Conferir e criar CP da taxa ${taxa != null ? formatMoeda(taxa) : ''}`}>
                            {conferindoCartao === r.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}Conferir
                          </button>
                        ))}
                      </span>
                    </td>
                  </tr>
                );
              }
              // ---- Desconhecido (venda finalizada sem recebimento) ----
              const d = row.r;
              return (
                <tr key={`d${d.orcamento_id}-${d.forma}`} style={{ backgroundColor: 'var(--devolucao-bg, #fff7ed)' }}>
                  <td style={{ fontSize: 12 }}>{rotuloMomento(d.finalizada_em ?? d.atualizado_em, dia)}</td>
                  <td style={{ fontSize: 12 }}>{d.forma === 'pix' ? 'Pix' : 'Cartão'}</td>
                  <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(d.valor)}</td>
                  <td style={{ fontSize: 12 }}><strong>{d.venda_numero ?? d.numero}</strong> {d.cliente_nome}</td>
                  <td style={{ fontSize: 12 }}>{celulaVendedor(d.vendedor_nome)}</td>
                  <td>{pillDesconhecido(d.conta_declarada)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      {podeVincular && d.forma === 'pix' && (
                        <button type="button" className="btn-secundario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => abrirVincularDesconhecido(d)} title="Casar com um Pix recebido no Mercado Pago">
                          <Link size={13} />Vincular
                        </button>
                      )}
                      {podeConferir && (
                        <button type="button" className="btn-primario" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => setModalDesc(d)} title="Informar onde o dinheiro caiu e conciliar">
                          <Check size={13} />Conferir
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modalPix && (
        <VincularModal
          recebimento={modalPix.r}
          modo={modalPix.modo}
          podeDesvincular={podeConferir}
          onClose={() => setModalPix(null)}
          onVinculado={() => void carregar()}
          onConferido={() => void carregar()}
        />
      )}
      {modalDesc && (
        <ConferirDesconhecidoModal
          alvo={modalDesc}
          contas={contasBanco}
          onClose={() => setModalDesc(null)}
          onConferido={() => void carregar()}
        />
      )}
      {comprovanteEstorno != null && (
        <ComprovanteEstornoModal cobrancaId={comprovanteEstorno} onClose={() => { setComprovanteEstorno(null); void carregar(); }} />
      )}
      {auditorAberto && <AuditorCartaoModal onClose={() => setAuditorAberto(false)} />}
      {estornarAlvo && (
        <ModalSenhaGerencial
          titulo="Estornar no cartão"
          subtitulo={`Devolve ${formatMoeda(Number(estornarAlvo.valor_pago ?? estornarAlvo.valor))} ao cartão de ${estornarAlvo.cliente_nome} — irreversível`}
          onConfirm={(senha) => void confirmarEstorno(senha)}
          onCancel={() => setEstornarAlvo(null)}
        />
      )}
      {identificarConta && (
        <ModalBuscarCliente
          onSelect={(c) => void vincularConta(c)}
          onClose={() => setIdentificarConta(null)}
        />
      )}
      {/* Devolucao de Pix (DA-506). O modal do valor fecha assim que a devolucao sai e da lugar ao
          desfecho — que segura o protocolo do MP na tela ate o operador fechar de proposito. */}
      {estornarPix && (
        <ModalEstornoPix
          recebimentoId={estornarPix.id}
          valorBruto={Number(estornarPix.valor)}
          saldoLivre={valorDisponivelPix(estornarPix)}
          pagadorNome={estornarPix.pagador_nome_exibicao ?? estornarPix.pagador_nome}
          clienteNome={estornarPix.cliente_conta_nome ?? null}
          onCancelar={() => setEstornarPix(null)}
          onFeito={(r) => { setEstornarPix(null); setDevolucaoFeita(r); void carregar(); }}
        />
      )}
      {devolucaoFeita && (
        <ComprovanteDevolucaoModal
          estorno={devolucaoFeita}
          onFechar={() => { setDevolucaoFeita(null); void carregar(); }}
        />
      )}
    </div>
  );
}
